/**
 * QuestManager.ts - Manages quest progression and story flags
 *
 * Coordinates between story events, NPC dialogues, and game rewards
 * Integrates with TransportManager for ship/canoe/airship rewards
 */

import {
  StoryFlag,
  StoryEvent,
  STORY_EVENTS,
  Location,
  checkStoryConditions,
  getStoryProgress,
  getCurrentAct,
  EventReward,
  DialogueLine,
} from './data/StoryData';

export interface QuestState {
  // Active story flags
  flags: Set<StoryFlag>;

  // Key items collected
  keyItems: Set<number>;

  // Current gil (gold)
  gil: number;

  // Current experience bonus (from events)
  experienceBonus: number;
}

export interface QuestEventResult {
  event: StoryEvent;
  dialogueLines: DialogueLine[];
  rewards: EventReward[];
  flagsSet: StoryFlag[];
}

export class QuestManager {
  private state: QuestState;

  // Callbacks for game integration
  private onFlagSet: ((flag: StoryFlag) => void) | null = null;
  private onReward: ((reward: EventReward) => void) | null = null;
  private onDialogue: ((lines: DialogueLine[]) => void) | null = null;

  constructor() {
    this.state = {
      flags: new Set<StoryFlag>(),
      keyItems: new Set<number>(),
      gil: 500, // Starting gold
      experienceBonus: 0,
    };
  }

  // =========================================================================
  // Flag Management
  // =========================================================================

  /**
   * Set a story flag
   */
  setFlag(flag: StoryFlag): void {
    if (!this.state.flags.has(flag)) {
      this.state.flags.add(flag);
      console.log(`[QuestManager] Flag set: ${StoryFlag[flag]}`);

      if (this.onFlagSet) {
        this.onFlagSet(flag);
      }
    }
  }

  /**
   * Check if a flag is set
   */
  hasFlag(flag: StoryFlag): boolean {
    return this.state.flags.has(flag);
  }

  /**
   * Get all active flags
   */
  getActiveFlags(): Set<StoryFlag> {
    return new Set(this.state.flags);
  }

  /**
   * Clear a flag (for debugging/testing)
   */
  clearFlag(flag: StoryFlag): void {
    this.state.flags.delete(flag);
    console.log(`[QuestManager] Flag cleared: ${StoryFlag[flag]}`);
  }

  // =========================================================================
  // Event Triggering
  // =========================================================================

  /**
   * Check for events at a location when entering
   */
  checkEnterMapEvents(location: Location): QuestEventResult | null {
    const events = STORY_EVENTS.filter(
      (event) =>
        event.location === location &&
        event.trigger.type === 'ENTER_MAP' &&
        checkStoryConditions(event.conditions, this.state.flags)
    );

    if (events.length > 0) {
      return this.executeEvent(events[0]);
    }

    return null;
  }

  /**
   * Check for events when talking to an NPC
   */
  checkTalkToNPCEvents(location: Location, npcId: string): QuestEventResult | null {
    const events = STORY_EVENTS.filter(
      (event) =>
        event.location === location &&
        event.trigger.type === 'TALK_TO_NPC' &&
        event.trigger.target === npcId &&
        checkStoryConditions(event.conditions, this.state.flags)
    );

    if (events.length > 0) {
      return this.executeEvent(events[0]);
    }

    return null;
  }

  /**
   * Check for events when defeating a boss
   */
  checkDefeatBossEvents(location: Location, bossId: string): QuestEventResult | null {
    const events = STORY_EVENTS.filter(
      (event) =>
        event.location === location &&
        event.trigger.type === 'DEFEAT_BOSS' &&
        event.trigger.target === bossId &&
        checkStoryConditions(event.conditions, this.state.flags)
    );

    if (events.length > 0) {
      return this.executeEvent(events[0]);
    }

    return null;
  }

  /**
   * Check for events when using an item
   */
  checkUseItemEvents(location: Location, itemId: string): QuestEventResult | null {
    const events = STORY_EVENTS.filter(
      (event) =>
        event.location === location &&
        event.trigger.type === 'USE_ITEM' &&
        event.trigger.target === itemId &&
        checkStoryConditions(event.conditions, this.state.flags)
    );

    if (events.length > 0) {
      return this.executeEvent(events[0]);
    }

    return null;
  }

  /**
   * Execute an event and process its effects
   */
  private executeEvent(event: StoryEvent): QuestEventResult {
    console.log(`[QuestManager] Executing event: ${event.name}`);

    // Process rewards
    const rewards = event.rewards || [];
    for (const reward of rewards) {
      this.processReward(reward);
    }

    // Set flags
    for (const flag of event.flagsToSet) {
      this.setFlag(flag);
    }

    // Trigger dialogue callback
    if (this.onDialogue && event.script.length > 0) {
      this.onDialogue(event.script);
    }

    return {
      event,
      dialogueLines: event.script,
      rewards,
      flagsSet: event.flagsToSet,
    };
  }

  /**
   * Process a reward
   */
  private processReward(reward: EventReward): void {
    switch (reward.type) {
      case 'GIL':
        if (reward.amount) {
          this.state.gil += reward.amount;
          console.log(`[QuestManager] Received ${reward.amount} gil`);
        }
        break;

      case 'EXPERIENCE':
        if (reward.amount) {
          this.state.experienceBonus += reward.amount;
          console.log(`[QuestManager] Received ${reward.amount} experience bonus`);
        }
        break;

      case 'KEY_ITEM':
        if (reward.id !== undefined) {
          this.state.keyItems.add(reward.id);
          console.log(`[QuestManager] Received key item ${reward.id}`);
        }
        break;

      case 'ITEM':
        console.log(`[QuestManager] Received item ${reward.id}`);
        break;
    }

    if (this.onReward) {
      this.onReward(reward);
    }
  }

  // =========================================================================
  // Key Item Checks
  // =========================================================================

  /**
   * Check if player has a key item
   */
  hasKeyItem(itemId: number): boolean {
    return this.state.keyItems.has(itemId);
  }

  /**
   * Get key item name by ID
   */
  getKeyItemName(itemId: number): string {
    const keyItemNames: Record<number, string> = {
      1: 'LUTE',
      2: 'CRYSTAL_EYE',
      3: 'JOLT_TONIC',
      4: 'MYSTIC_KEY',
      5: 'STAR_RUBY',
      6: 'EARTH_ROD',
      7: 'CANOE',
      8: 'LEVISTONE',
      9: 'CROWN',
      10: 'RAT_TAIL',
      11: 'SLAB',
      12: 'CUBE',
      13: 'CHIME',
      14: 'OXYALE',
      15: 'SHIP',
      16: 'AIRSHIP',
    };
    return keyItemNames[itemId] || 'UNKNOWN';
  }

  // =========================================================================
  // Progress Queries
  // =========================================================================

  /**
   * Get story progress percentage
   */
  getProgress(): number {
    return getStoryProgress(this.state.flags);
  }

  /**
   * Get current act number (1-5)
   */
  getCurrentAct(): number {
    return getCurrentAct(this.state.flags);
  }

  /**
   * Get current gil
   */
  getGil(): number {
    return this.state.gil;
  }

  /**
   * Modify gil
   */
  modifyGil(amount: number): void {
    this.state.gil = Math.max(0, this.state.gil + amount);
  }

  /**
   * Get transport unlock status
   */
  getTransportStatus(): { ship: boolean; canoe: boolean; airship: boolean } {
    return {
      ship: this.hasFlag(StoryFlag.GOT_SHIP),
      canoe: this.hasFlag(StoryFlag.GOT_CANOE),
      airship: this.hasFlag(StoryFlag.GOT_AIRSHIP),
    };
  }

  /**
   * Check if all crystals are restored
   */
  allCrystalsRestored(): boolean {
    return this.hasFlag(StoryFlag.ALL_CRYSTALS_RESTORED);
  }

  /**
   * Get individual crystal status
   */
  getCrystalStatus(): { earth: boolean; fire: boolean; water: boolean; wind: boolean } {
    return {
      earth: this.hasFlag(StoryFlag.RESTORED_EARTH_CRYSTAL),
      fire: this.hasFlag(StoryFlag.RESTORED_FIRE_CRYSTAL),
      water: this.hasFlag(StoryFlag.RESTORED_WATER_CRYSTAL),
      wind: this.hasFlag(StoryFlag.RESTORED_WIND_CRYSTAL),
    };
  }

  // =========================================================================
  // Boss Defeat Helpers
  // =========================================================================

  /**
   * Record defeating a boss and check for related events
   */
  recordBossDefeat(bossId: string, location: Location): QuestEventResult | null {
    console.log(`[QuestManager] Boss defeated: ${bossId} at ${location}`);
    return this.checkDefeatBossEvents(location, bossId);
  }

  // =========================================================================
  // Callbacks
  // =========================================================================

  /**
   * Set callback for when a flag is set
   */
  onFlagSetCallback(callback: (flag: StoryFlag) => void): void {
    this.onFlagSet = callback;
  }

  /**
   * Set callback for when a reward is given
   */
  onRewardCallback(callback: (reward: EventReward) => void): void {
    this.onReward = callback;
  }

  /**
   * Set callback for when dialogue should be shown
   */
  onDialogueCallback(callback: (lines: DialogueLine[]) => void): void {
    this.onDialogue = callback;
  }

  // =========================================================================
  // Serialization
  // =========================================================================

  /**
   * Get state for saving
   */
  getState(): { flags: number[]; keyItems: number[]; gil: number; experienceBonus: number } {
    return {
      flags: Array.from(this.state.flags),
      keyItems: Array.from(this.state.keyItems),
      gil: this.state.gil,
      experienceBonus: this.state.experienceBonus,
    };
  }

  /**
   * Restore state from save
   */
  setState(state: { flags: number[]; keyItems: number[]; gil: number; experienceBonus: number }): void {
    this.state = {
      flags: new Set(state.flags as StoryFlag[]),
      keyItems: new Set(state.keyItems),
      gil: state.gil,
      experienceBonus: state.experienceBonus,
    };
    console.log(`[QuestManager] State restored: ${this.state.flags.size} flags, ${this.state.keyItems.size} key items`);
  }

  /**
   * Reset to initial state (for new game)
   */
  reset(): void {
    this.state = {
      flags: new Set<StoryFlag>(),
      keyItems: new Set<number>(),
      gil: 500,
      experienceBonus: 0,
    };
    console.log('[QuestManager] Reset to initial state');
  }

  // =========================================================================
  // Debug
  // =========================================================================

  /**
   * Debug: Set multiple flags at once
   */
  debugSetFlags(flags: StoryFlag[]): void {
    for (const flag of flags) {
      this.setFlag(flag);
    }
    console.log(`[QuestManager] DEBUG: Set ${flags.length} flags`);
  }

  /**
   * Debug: Skip to a specific act
   */
  debugSkipToAct(act: number): void {
    const actFlags: Record<number, StoryFlag[]> = {
      1: [StoryFlag.GAME_START, StoryFlag.ARRIVED_CORNELIA],
      2: [
        StoryFlag.GAME_START, StoryFlag.ARRIVED_CORNELIA, StoryFlag.DEFEATED_GARLAND,
        StoryFlag.RESCUED_PRINCESS, StoryFlag.RECEIVED_LUTE, StoryFlag.BRIDGE_BUILT,
        StoryFlag.DEFEATED_PIRATES, StoryFlag.GOT_SHIP,
      ],
      3: [
        StoryFlag.GAME_START, StoryFlag.ARRIVED_CORNELIA, StoryFlag.DEFEATED_GARLAND,
        StoryFlag.RESCUED_PRINCESS, StoryFlag.RECEIVED_LUTE, StoryFlag.BRIDGE_BUILT,
        StoryFlag.DEFEATED_PIRATES, StoryFlag.GOT_SHIP, StoryFlag.DEFEATED_LICH,
        StoryFlag.RESTORED_EARTH_CRYSTAL, StoryFlag.MET_CIRCLE_OF_SAGES, StoryFlag.GOT_CANOE,
        StoryFlag.GOT_LEVISTONE, StoryFlag.GOT_AIRSHIP,
      ],
      4: [
        StoryFlag.GAME_START, StoryFlag.ARRIVED_CORNELIA, StoryFlag.DEFEATED_GARLAND,
        StoryFlag.RESCUED_PRINCESS, StoryFlag.RECEIVED_LUTE, StoryFlag.BRIDGE_BUILT,
        StoryFlag.DEFEATED_PIRATES, StoryFlag.GOT_SHIP, StoryFlag.DEFEATED_LICH,
        StoryFlag.RESTORED_EARTH_CRYSTAL, StoryFlag.MET_CIRCLE_OF_SAGES, StoryFlag.GOT_CANOE,
        StoryFlag.GOT_LEVISTONE, StoryFlag.GOT_AIRSHIP, StoryFlag.CLASS_UPGRADE,
      ],
      5: [
        StoryFlag.GAME_START, StoryFlag.ARRIVED_CORNELIA, StoryFlag.DEFEATED_GARLAND,
        StoryFlag.RESCUED_PRINCESS, StoryFlag.RECEIVED_LUTE, StoryFlag.BRIDGE_BUILT,
        StoryFlag.DEFEATED_PIRATES, StoryFlag.GOT_SHIP, StoryFlag.DEFEATED_LICH,
        StoryFlag.RESTORED_EARTH_CRYSTAL, StoryFlag.MET_CIRCLE_OF_SAGES, StoryFlag.GOT_CANOE,
        StoryFlag.GOT_LEVISTONE, StoryFlag.GOT_AIRSHIP, StoryFlag.CLASS_UPGRADE,
        StoryFlag.DEFEATED_KARY, StoryFlag.RESTORED_FIRE_CRYSTAL,
        StoryFlag.DEFEATED_KRAKEN, StoryFlag.RESTORED_WATER_CRYSTAL,
        StoryFlag.DEFEATED_TIAMAT, StoryFlag.RESTORED_WIND_CRYSTAL,
        StoryFlag.ALL_CRYSTALS_RESTORED,
      ],
    };

    const flags = actFlags[act] || actFlags[1];
    this.debugSetFlags(flags);
    console.log(`[QuestManager] DEBUG: Skipped to Act ${act}`);
  }

  /**
   * Debug: Print current state
   */
  debugPrintState(): void {
    console.log('=== Quest Manager State ===');
    console.log(`Progress: ${this.getProgress()}%`);
    console.log(`Current Act: ${this.getCurrentAct()}`);
    console.log(`Gil: ${this.state.gil}`);
    console.log(`Flags (${this.state.flags.size}):`, Array.from(this.state.flags).map(f => StoryFlag[f]).join(', '));
    console.log(`Key Items (${this.state.keyItems.size}):`, Array.from(this.state.keyItems).map(id => this.getKeyItemName(id)).join(', '));
    console.log(`Transports:`, this.getTransportStatus());
    console.log(`Crystals:`, this.getCrystalStatus());
    console.log('===========================');
  }
}
